import { axios } from '../request'

const url = 'membership_cards'

// #418 — a 2xx whose body is not the `{"data": …}` envelope leaves
// `res.data.data` undefined (axios keeps the raw text when `JSON.parse` throws,
// so an empty response arrives as `''` and a proxy's error page as a string).
//
// `verify` committed that straight into `verificationData`, which was born `{}`.
// The consumers' guard IS the throwing read — `Verify.vue:24` and
// `VerifyMultiple.vue:13` both gate on `verifyData._id`, which is a TypeError
// the moment the state is undefined — so the public card-verification page went
// blank instead of saying the card could not be verified.
//
// `get` and `getAll` are the same defect one level down: both reach for
// `res.data.data.url`, which throws on the very same bodies.
const isRecord = (data) => data !== null && typeof data === 'object' && !Array.isArray(data)
const cardURL = (res) => {
  const link = res?.data?.data?.url
  return typeof link === 'string' && link !== '' ? link : null
}

// A 404 is how these endpoints report "no such card" (VerifierController), not
// a fault: the card-verification page is public and gets scanned with whatever
// a camera reads, so an unknown id is an ordinary answer and logging it would
// shout on every mis-scan.
const logFailure = (endpoint, err) => {
  if (err?.response?.status !== 404) {
    console.error(`membershipCards: ${endpoint} failed`, err)
  }
}

export default (app) => {
  const apiURL = app.config.globalProperties.$apiURL
  return {
    namespaced: true,
    state: {
      verifyNotFound: false,
      verifyLoading: false,
      verifyLoaded: false,
      verificationData: {},
    },
    mutations: {
      setVerificationData(state, data) {
        state.verificationData = data
      },
      verifyLoaded(state, data) {
        state.verifyLoaded = data
      },
      verifyLoading(state, data) {
        state.verifyLoading = data
      },
      verifyNotFound(state, data) {
        state.verifyNotFound = data
      },
    },
    actions: {
      get(_context, id) {
        // Settles, unlike the `new Promise(() => {})` it replaces: that one
        // could not resolve even after a successful redirect, and on a bad body
        // the `res.data.data.url` read threw into a rejection nobody held —
        // leaving `Single.vue` on "A redireccionar…" with no way to know.
        return new Promise((resolve) => {
          axios
            .get(url + '/' + id)
            .then((res) => {
              const link = cardURL(res)
              if (link === null) {
                console.error(
                  `membershipCards: ${url}/${id} answered ${res?.status} without a card URL`,
                  res?.data
                )
                resolve()
                return
              }
              window.location.replace(apiURL + '/' + link)
              resolve()
            })
            .catch((err) => {
              logFailure(`${url}/${id}`, err)
              resolve()
            })
        })
      },
      getAll(_context, { type, includeLetters }) {
        return new Promise((resolve, reject) => {
          axios
            .get(`${url}?type=${type}&includeLetters=${includeLetters}`)
            .then((res) => {
              const link = cardURL(res)
              if (link === null) {
                console.error(
                  `membershipCards: ${url} answered ${res?.status} without a card URL`,
                  res?.data
                )
                // Rejecting rather than resolving: the caller flips the page
                // into "A Gerar todos os cartões..." on resolve, and there is
                // nothing being generated.
                reject(new Error('Could not generate cards'))
                return
              }
              resolve(apiURL + '/' + link)
            })
            .catch((err) => {
              logFailure(url, err)
              // #422 — carry the axios error as the cause, as `update`/`save`
              // do since #414. `List.vue` now tells the user why the generation
              // was refused instead of leaving the overlay open saying nothing,
              // and a bare message would leave it with only its fallback.
              reject(new Error('Could not generate cards', { cause: err }))
            })
        })
      },
      verify(context, id) {
        context.commit('verifyLoaded', false)
        context.commit('verifyLoading', true)
        context.commit('verifyNotFound', false)
        // Returned, so the action is awaitable: without this Vuex wrapped the
        // undefined return in an already-resolved promise, and
        // `VerifyMultiple.vue`'s `.then()` ran before the request had left.
        return axios
          .get(url + '/verify/' + id)
          .then((res) => {
            if (!isRecord(res?.data?.data)) {
              // The request succeeded, so there was no error to swallow and no
              // trace anywhere — the payload simply was not there. Falls back
              // to the same state a 404 produces, which is the honest one: we
              // could not verify this card.
              console.error(
                `membershipCards: ${url}/verify/${id} answered ${res?.status} without a usable envelope`,
                res?.data
              )
              notVerified(context)
              return
            }
            context.commit('setVerificationData', res.data.data)
            context.commit('verifyLoaded', true)
            context.commit('verifyLoading', false)
          })
          .catch((err) => {
            logFailure(`${url}/verify/${id}`, err)
            notVerified(context)
          })
      },
    },
  }
}

// notVerified is the "we have nothing to show for this card" state: an empty
// record of the type `verificationData` was born with, loaded so the spinner
// stops, and flagged not-found so the page renders its "Inactivo ou Inexistente
// / Invalid or Inactive Member" panel instead of a blank screen.
function notVerified(context) {
  context.commit('setVerificationData', {})
  context.commit('verifyLoaded', true)
  context.commit('verifyLoading', false)
  context.commit('verifyNotFound', true)
}
